import i18n from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import type { User } from "@supabase/supabase-js";
import { motion, AnimatePresence } from "framer-motion";
import { 
  X, 
  User as UserIcon, 
  LogOut, 
  LayoutGrid, 
  ChevronRight, 
  CalendarCheck,
  Settings,
  Tag,
  Sun,
  Moon,
  Bell,
  ShieldCheck,
  Shield,
  MessageSquareText,
  Headphones,
  Wallet,
  Building2,
  CalendarDays,
} from "lucide-react";
import { useTheme } from "next-themes";
import NotificationBell from "@/components/notifications/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useTranslation } from "react-i18next";


import { tAuto } from "@/lib/tAuto";
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  getInitials: () => string;
  getAvatarUrl: () => string | null;
  onSignOut: () => Promise<void> | void;
};

interface MenuItemConfig {
  to: string;
  icon: React.ComponentType<any>;
  label: string;
  description: string;
  gradient: string;
}

export default function DesktopUserPanel({
  open,
  onOpenChange,
  user,
  getInitials,
  getAvatarUrl,
  onSignOut,
}: Props) {
  const { t } = useTranslation();
  const close = () => {
    onOpenChange(false);
  };
  
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const { isHost, isGuest, isSuperAdmin } = useUserRoles();
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  // Build menu items based on roles
  const menuItems: MenuItemConfig[] = [];

  // Fazer Oferta: only for guests and super admin
  if (isGuest || isSuperAdmin) {
    menuItems.push({
      to: "/ofertas/nova",
      icon: Tag,
      label: t('nav.makeOffer'),
      description: t('userPanel.makeOfferDesc'),
      gradient: "from-rose-500 to-pink-500",
    });
  }

  menuItems.push({
    to: "/perfil",
    icon: UserIcon,
    label: t('nav.profile'),
    description: t('userPanel.profileDesc'),
    gradient: "from-orange-500 to-amber-500",
  });

  // Minhas Reservas: only for guests and super admin
  if (isGuest || isSuperAdmin) {
    menuItems.push({
      to: "/reservas",
      icon: CalendarCheck,
      label: t('nav.myBookings'),
      description: t('userPanel.bookingsDesc'),
      gradient: "from-blue-500 to-cyan-500",
    });
  }


  // Painel Proprietário: only for hosts and super admin
  if (isHost || isSuperAdmin) {
    menuItems.push({
      to: "/dashboard/anuncios",
      icon: Building2,
      label: i18n.t("components.ads") || "Anúncios",
      description: i18n.t("components.manageAds") || "Gerencie os seus anúncios e imóveis",
      gradient: "from-emerald-500 to-teal-500",
    });
    menuItems.push({
      to: "/dashboard/reservas",
      icon: CalendarCheck,
      label: "Reservas",
      description: i18n.t("components.manageStays") || "Gerencie pedidos e estadias de hóspedes",
      gradient: "from-amber-500 to-orange-500",
    });
    menuItems.push({
      to: "/dashboard/calendario",
      icon: CalendarDays,
      label: "Calendário",
      description: "Visualize datas ocupadas e reservadas",
      gradient: "from-primary to-purple-500",
    });
    menuItems.push({
      to: "/dashboard/financeiro",
      icon: Wallet,
      label: "Ganhos",
      description: "Acompanhe os seus rendimentos e saques",
      gradient: "from-blue-500 to-primary",
    });
    menuItems.push({
      to: "/seguro",
      icon: ShieldCheck,
      label: "Meu Seguro",
      description: tAuto("auto.components_layout_desktopuserpanel.1", "Proteja o seu imóvel com seguro"),
      gradient: "from-[#E8513D] to-[#B83A28]",
    });
  }

  // Admin access: only for super admin
  if (isSuperAdmin) {
    menuItems.push({
      to: "/admin",
      icon: Shield,
      label: "Painel Administrativo",
      description: tAuto("auto.components_layout_desktopuserpanel.2", "Gestão interna da plataforma"),
      gradient: "from-slate-600 to-slate-800",
    });
  }

  // Configurações
  menuItems.push({
    to: "/configuracoes",
    icon: Settings,
    label: t('nav.settings'),
    description: t('userPanel.settingsDesc'),
    gradient: "from-slate-500 to-gray-500",
  });

  // Lock body scroll when panel is open + notify overlay store so
  // floating widgets (Bera IA) hide behind it.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    import("@/lib/kycUiState").then(({ kycUiStore }) => {
      kycUiStore.setDesktopMenuOpen(open);
    });
    return () => {
      document.body.style.overflow = "";
      import("@/lib/kycUiState").then(({ kycUiStore }) => {
        kycUiStore.setDesktopMenuOpen(false);
      });
    };
  }, [open]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) close();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open]);

  const panelContent = (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={close}
          />

          {/* Panel Container */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ 
              type: "spring", 
              damping: 30, 
              stiffness: 300,
              mass: 0.8
            }}
            className="absolute top-0 right-0 h-full w-[380px] bg-gradient-to-b from-background via-background to-muted/30 shadow-2xl"
          >
            {/* Decorative gradient orb */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 via-bera-gold/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            
            {/* Content */}
            <div className="relative h-full flex flex-col w-full overflow-hidden">
                <>
                  {/* Header with close button */}
              <div className="flex items-center justify-between p-5 border-b border-border/50">
                <motion.span 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-sm font-medium text-muted-foreground"
                >
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('userPanel.myAccount')}
                  </span>
                </motion.span>
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  onClick={close}
                  className="w-9 h-9 rounded-full bg-muted/60 hover:bg-muted flex items-center justify-center transition-colors"
                  aria-label={t('common.cancel')}
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </motion.button>
              </div>

              {/* User Profile Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="p-5"
              >
                <div className="relative p-5 rounded-2xl bg-gradient-to-br from-primary/5 via-muted/50 to-bera-gold/5 border border-border/50 overflow-hidden">
                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20">
                    <div className="absolute top-2 right-2 w-16 h-16 bg-gradient-to-br from-bera-gold/20 to-transparent rounded-full blur-xl" />
                  </div>
                  
                  <div className="relative flex items-center gap-4">
                    {/* Avatar with ring animation */}
                    <div className="relative">
                      <motion.div
                        animate={{ 
                          boxShadow: [
                            "0 0 0 0 rgba(234, 88, 12, 0.4)",
                            "0 0 0 8px rgba(234, 88, 12, 0)",
                          ]
                        }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity,
                          ease: "easeOut"
                        }}
                        className="rounded-full"
                      >
                        <Avatar className="h-16 w-16 border-2 border-primary/30 shadow-xl">
                          {getAvatarUrl() && (
                            <AvatarImage 
                              src={getAvatarUrl()!} 
                              alt="Avatar"
                              className="object-cover"
                            />
                          )}
                          <AvatarFallback className="gradient-primary text-white font-bold text-xl">
                            {getInitials()}
                          </AvatarFallback>
                        </Avatar>
                      </motion.div>
                      {/* Online indicator */}
                      <span className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-background" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground text-lg truncate">
                        {user.user_metadata?.full_name || t('common.user')}
                      </h3>
                      <p className="text-sm text-muted-foreground truncate mb-1">
                        {user.email}
                      </p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary border border-primary/20">
                        {isSuperAdmin ? "Super Admin" : isHost ? t('common.host') : t('common.guest')}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="px-5 pb-3"
              >
                <button
                  type="button"
                  onClick={() => {
                    close();
                    window.setTimeout(() => setNotifModalOpen(true), 320);
                  }}
                  className="flex w-full items-center justify-center gap-2 py-2.5 rounded-xl bg-muted/50 hover:bg-muted transition-colors group cursor-pointer text-left"
                >
                  <Bell className="w-5 h-5 text-foreground shrink-0" strokeWidth={1.5} />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    {t('nav.notifications')}
                  </span>
                </button>
              </motion.div>

              {/* Menu Items */}
              <div className="flex-1 overflow-y-auto px-5 py-2">
                <div className="space-y-2">
                  {menuItems.map((item, index) => (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + index * 0.05 }}
                    >
                      <Link
                        to={item.to}
                        onClick={close}
                        className="group flex items-center gap-4 p-3.5 rounded-xl bg-card/50 hover:bg-card border border-transparent hover:border-border/50 transition-all duration-200 hover:shadow-lg"
                      >
                        <div className={cn(
                          "w-11 h-11 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200",
                          item.gradient
                        )}>
                          <item.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                            {item.label}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {item.description}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Support + Theme Toggle + Sign Out */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mt-6 pt-5 border-t border-border/50 space-y-3 pb-6"
                >
                  {/* Suporte Premium Button */}
                  <div className="w-full">
                    <div className="w-full flex items-center justify-between p-3 rounded-2xl bg-white dark:bg-card border border-border shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#E8513D]/10 flex items-center justify-center shrink-0">
                          <Headphones className="w-5 h-5 text-[#E8513D]" />
                        </div>
                        <div className="flex flex-col text-left">
                          <span className="font-bold text-foreground text-sm leading-tight">Suporte</span>
                          <span className="text-xs text-muted-foreground mt-0.5">{tAuto("auto.components_layout_desktopuserpanel.3", "Estamos aqui para ajudar")}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          window.dispatchEvent(new CustomEvent('open-bera-ia-widget'));
                          close();
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-muted/50 rounded-full border border-border shadow-sm text-[#E8513D] hover:bg-gray-50 dark:hover:bg-muted transition-colors shrink-0"
                      >
                        <span className="font-bold text-[13px]">Bera</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>


                  <button
                    onClick={async () => {
                      close();
                      await onSignOut();
                    }}
                    className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-destructive/10 hover:bg-destructive/15 border border-destructive/20 text-destructive transition-all duration-200 group"
                  >
                    <LogOut className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    <span className="font-medium">{t('common.logout')}</span>
                  </button>
                </motion.div>
              </div>
            </>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Portal to document body
  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      {panelContent}
      <NotificationBell
        dialogOnly
        open={notifModalOpen}
        onOpenChange={setNotifModalOpen}
      />
    </>,
    document.body
  );
}
